/**
 * DateFilterDropdown Component
 *
 * Reusable date filter with preset options and custom range
 * Task: F.2 - Date Filter Component
 *
 * Presets: today, yesterday, last3days, last7days, custom
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  Button,
  Typography,
  Chip
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';

// Preset configurations
const DATE_PRESETS = [
  { value: 'today', label: 'Today', getRange: () => ({ from: moment().startOf('day'), to: moment().endOf('day') }) },
  { value: 'yesterday', label: 'Yesterday', getRange: () => ({ from: moment().subtract(1, 'day').startOf('day'), to: moment().subtract(1, 'day').endOf('day') }) },
  { value: 'last3days', label: 'Last 3 Days', getRange: () => ({ from: moment().subtract(3, 'days').startOf('day'), to: moment().endOf('day') }) },
  { value: 'last7days', label: 'Last 7 Days', getRange: () => ({ from: moment().subtract(7, 'days').startOf('day'), to: moment().endOf('day') }) },
  { value: 'custom', label: 'Custom Range', getRange: () => null }
];

/**
 * DateFilterDropdown
 *
 * @param {Object} props
 * @param {string} props.value - Current preset value
 * @param {Function} props.onChange - Callback: (preset, dateRange) => void
 * @param {Object} props.customRange - { from: Date, to: Date } for custom preset
 * @param {Array} props.presets - Optional custom presets array (defaults to all)
 * @param {Object} props.sx - Additional MUI sx styling
 */
const DateFilterDropdown = ({
  value = 'today',
  onChange,
  customRange,
  presets = DATE_PRESETS,
  sx = {}
}) => {
  const [selectedPreset, setSelectedPreset] = useState(value);
  const [customFrom, setCustomFrom] = useState(customRange?.from ? moment(customRange.from) : moment().subtract(7, 'days'));
  const [customTo, setCustomTo] = useState(customRange?.to ? moment(customRange.to) : moment());
  const [anchorEl, setAnchorEl] = useState(null);

  // Sync with external value
  useEffect(() => {
    if (value !== selectedPreset) {
      setSelectedPreset(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Get current date range label
  const getDisplayLabel = () => {
    if (selectedPreset === 'custom') {
      return `${customFrom.format('MMM D')} - ${customTo.format('MMM D, YYYY')}`;
    }
    const preset = presets.find(p => p.value === selectedPreset);
    return preset?.label || 'Select Date';
  };

  // Handle preset selection
  const handlePresetChange = (event) => {
    const newPreset = event.target.value;
    setSelectedPreset(newPreset);

    if (newPreset === 'custom') {
      // Open custom range popover
      setAnchorEl(event.currentTarget);
    } else {
      // Calculate and emit date range
      const presetConfig = presets.find(p => p.value === newPreset);
      const range = presetConfig?.getRange();
      if (onChange && range) {
        onChange(newPreset, {
          from: range.from.toDate(),
          to: range.to.toDate()
        });
      }
    }
  };

  // Handle custom range apply
  const handleCustomApply = () => {
    setAnchorEl(null);
    if (onChange) {
      onChange('custom', {
        from: customFrom.toDate(),
        to: customTo.toDate()
      });
    }
  };

  // Handle popover close
  const handlePopoverClose = () => {
    setAnchorEl(null);
    // Revert to previous preset if custom not applied
    if (selectedPreset === 'custom' && !customRange) {
      setSelectedPreset('today');
    }
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="date-filter-label" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Date Range
          </InputLabel>
          <Select
            labelId="date-filter-label"
            value={selectedPreset}
            label="Date Range"
            onChange={handlePresetChange}
            startAdornment={
              <CalendarTodayIcon sx={{ color: 'rgba(255,255,255,0.5)', mr: 1, fontSize: 18 }} />
            }
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7c3aed',
              },
              '.MuiSvgIcon-root': {
                color: 'rgba(255,255,255,0.5)',
              }
            }}
          >
            {presets.map((preset) => (
              <MenuItem key={preset.value} value={preset.value}>
                {preset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Display current range as chip */}
        {selectedPreset && (
          <Chip
            label={getDisplayLabel()}
            size="small"
            sx={{
              backgroundColor: 'rgba(124, 58, 237, 0.2)',
              color: '#a78bfa',
              border: '1px solid rgba(124, 58, 237, 0.3)',
              fontWeight: 500
            }}
          />
        )}

        {/* Custom Range Popover */}
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              backgroundColor: '#292b2f',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              p: 2,
              mt: 1
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
            Select Custom Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DatePicker
              label="From Date"
              value={customFrom}
              onChange={(newValue) => setCustomFrom(newValue)}
              maxDate={customTo}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' }
                  }
                }
              }}
            />

            <DatePicker
              label="To Date"
              value={customTo}
              onChange={(newValue) => setCustomTo(newValue)}
              minDate={customFrom}
              maxDate={moment()}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' }
                  }
                }
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                size="small"
                onClick={handlePopoverClose}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleCustomApply}
                sx={{
                  backgroundColor: '#7c3aed',
                  '&:hover': { backgroundColor: '#6d28d9' }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DateFilterDropdown;
export { DATE_PRESETS };
